import { FormField, ModalDialog, type ImperialLengthUnit, type LengthUnit } from "@rothoblaas/design-system";
import { useState, type FC } from "react";
import { Controller, useFormContext, type FieldError, type Validate } from "react-hook-form";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import LengthInput from "./LengthInput";
import styles from "./NumericField.module.css";

type NumericFieldProps = {
  name: "base" | "height" | "secondaryBase" | "secondaryHeight" | "totalHeight" | "slopeDegrees";
  label: string;
  error?: FieldError;
  suffix?: string;
  defaultValue: number | null;
  hasShowHelp?: boolean;
  helpImage?: string;
  isRectangular: boolean;
  accentColor: string;
  decimalScale?: number;
  minValue?: number;
  maxValue?: number;
  validate?: Validate<number | null, DeckDesignerConfigurationFormValues>;
  // Passed in rather than watched here, so the card subscribes to the unit of measure once for all its fields.
  isImperial: boolean;
  // Set on lengths: in imperial the field shows imperialUnit, the form still gets metricUnit.
  metricUnit?: LengthUnit;
  imperialUnit?: ImperialLengthUnit;
  maxDenominator?: number;
  // When set, the field is read-only and edited elsewhere: clicking it (or Enter/Space) calls this.
  onOpen?: () => void;
};

// ShapeAndSizeCard's numeric fields, each with an optional help image.
const NumericField: FC<NumericFieldProps> = ({
  name,
  label,
  error,
  suffix = "",
  defaultValue,
  hasShowHelp = false,
  helpImage,
  isRectangular,
  accentColor,
  validate,
  decimalScale = 3,
  minValue,
  maxValue,
  isImperial,
  metricUnit,
  imperialUnit,
  maxDenominator,
  onOpen,
}) => {
  const { control } = useFormContext<DeckDesignerConfigurationFormValues>();

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const rules = {
    min: minValue ?? 0,
    ...(maxValue !== undefined ? { max: maxValue } : {}),
    validate: (value: number | null, formValues: DeckDesignerConfigurationFormValues) =>
      validate?.(value, formValues) ?? true,
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field }) => (
          <FormField
            showHelp={hasShowHelp ? { onOpen: () => setIsHelpOpen(true) } : undefined}
            label={label}
            error={error}
          >
            {({ labelId, isInvalid }) => (
              <LengthInput
                {...field}
                id={labelId}
                isImperial={isImperial}
                metricUnit={metricUnit}
                imperialUnit={imperialUnit}
                maxDenominator={maxDenominator}
                decimalScale={decimalScale}
                suffix={suffix}
                step={0.01}
                min={minValue ?? 0.1}
                allowNegative={false}
                isInvalid={isInvalid}
                onValueChange={field.onChange}
                {...(onOpen
                  ? {
                      className: styles.openableField,
                      readOnly: true,
                      onClick: onOpen,
                      onKeyDown: (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onOpen();
                        }
                      },
                    }
                  : null)}
              />
            )}
          </FormField>
        )}
      />
      {isHelpOpen && (
        <ModalDialog onClose={() => setIsHelpOpen(false)} fitContent accentColor={accentColor}>
          <img
            className={styles.helpImage}
            src={
              helpImage ?? (isRectangular ? "/deckDesigner/rectangular-sizes.png" : "/deckDesigner/Lshaped-sizes.png")
            }
          />
        </ModalDialog>
      )}
    </>
  );
};

export default NumericField;
